/**
 * Phase-5 pod room scene.
 *
 * Pixi-rendered "office" view for a single pod -- the PM sits in the
 * centre, the six analyst agents (QA / SA / FA / Researcher / RM / EA)
 * arrange around it.  Speech bubbles appear above each agent for
 * thinking (``…``) and the latest signal (top tickers); the PM bubble
 * carries its ``ui_summary`` or rationale.
 *
 * The scene is fully data-driven: every redraw reads
 * ``ctx.getState().selectedPod`` and rebuilds positions / bubbles from
 * scratch.  Layout adapts to viewport size:
 *
 *   wide   (>= 600 px) — agents on a true hex ring around the PM;
 *                        PM diameter ~22 % of the smaller viewport axis.
 *   narrow (<  600 px) — PM at the top, agents in a 2x3 grid below
 *                        (mirrors the DOM ``PodRoomView`` mobile layout).
 *
 * Animation cadence:
 *   * Bubble visibility / contents are recomputed every frame in
 *     ``tick`` so the TTL fade still works while the user idles.
 *   * Pulse animation for ``thinking`` agents is handled by Pixi
 *     scale tween (sin-based) -- no React state in the hot path.
 */

import { Container, FillGradient, Graphics, Text, TextStyle } from 'pixi.js';
import type {
  Scene,
  SceneAgent,
  SceneContext,
  SceneOrder,
  SceneSelectedPod,
} from './SceneManager';
import { intensityToMultiplier } from './SceneManager';
import { drawFace, drawRoleIcon } from './characters';
import {
  LOOK_DURATION_MS,
  pickLookOffset,
  type LookSpeaker,
} from './podRoomLook';
import {
  clearPodRoomAgentRects,
  setPodRoomAgentRects,
  type AgentHitRect,
} from './podRoomHitRects';

/**
 * Floor accent gradients (top → bottom rgb).  Top stops are deliberately
 * desaturated -- a bright accent at full intensity washes out the agent
 * rings.  Bottom stops fade to near-black for a clean horizon.
 */
const FLOOR_GRADIENT: Record<string, [number, number]> = {
  canary_lab: [0x12253e, 0x0a0e14],
  active_offices: [0x131e36, 0x0a0e14],
  reduced_floor: [0x2a1f14, 0x0a0e14],
  frozen_basement: [0x1a2c46, 0x0a0e14],
  default: [0x1a1f29, 0x0a0e14],
};

/** Mood → accent hex for agent rings + bubble borders. */
const MOOD_COLOR: Record<string, number> = {
  idle: 0x8b949e,
  thinking: 0x58a6ff,
  confident: 0x2ea043,
  worried: 0xd29922,
  excited: 0xdb61a2,
  angry: 0xf85149,
  proud: 0xa371f7,
  sad: 0x6e7681,
  frozen: 0x58a6ff,
  celebrating: 0xffd33d,
};

/** Reading order around the PM -- starts at top, clockwise. */
const AGENT_RING: { actor: string; label: string }[] = [
  { actor: 'qa', label: 'Quant' },
  { actor: 'sa', label: 'Sentiment' },
  { actor: 'fa', label: 'Fundamental' },
  { actor: 'researcher', label: 'Researcher' },
  { actor: 'rm', label: 'Risk Mgr' },
  { actor: 'ea', label: 'Execution' },
];

const SIGNAL_BUBBLE_TTL_MS = 8_000;
const PM_BUBBLE_TTL_MS = 12_000;
const ORDER_CHIP_VISIBLE_MAX = 8;
/** Border + fill per order type (Phase 7 ticker rail). */
const ORDER_COLOR: Record<SceneOrder['type'], { stroke: number; bg: number }> = {
  submitted: { stroke: 0x58a6ff, bg: 0x101a2d },
  filled: { stroke: 0x2ea043, bg: 0x0e1f17 },
  rejected: { stroke: 0xf85149, bg: 0x2a1010 },
};

interface AgentSprite {
  actor: string;
  container: Container;
  ring: Graphics;
  /** Mood-driven face features (eyes + mouth), repainted every frame. */
  face: Graphics;
  /** Role-specific accessory above the head (gear / shield / arrow / …). */
  roleIcon: Graphics;
  /** One-shot expanding halo painted for ~800 ms after a signal lands. */
  burst: Graphics;
  caption: Text;
  bubbleBg: Graphics;
  bubbleText: Text;
}

interface PmSprite {
  container: Container;
  ring: Graphics;
  face: Graphics;
  roleIcon: Graphics;
  /** Ray burst painted briefly when ``pm.decision_made`` lands. */
  burst: Graphics;
  caption: Text;
  bubbleBg: Graphics;
  bubbleText: Text;
  /** Tiny "Z" mark shown over the PM when the pod has been dormant
   *  past ``POD_DORMANT_THRESHOLD_MS`` (or sun is down).  Hidden
   *  otherwise. */
  zzz: Text;
}

const SIGNAL_BURST_MS = 800;
const DECISION_BURST_MS = 1_200;
/** How long an order's "flying paper" stays in flight from EA -> rail. */
const ORDER_FLIGHT_MS = 900;

/** Window after the latest ``agent.idle_action`` during which the
 *  character plays a kind-specific reaction (eye saccade, head tilt).
 *  After it expires they drop back into ambient breathing. */
const IDLE_REACTION_MS = 1_400;
/** No event on the pod for this long -> agents drift into a sleeping
 *  pose (eyes closed + ``z`` mark over the PM). */
const POD_DORMANT_THRESHOLD_MS = 120_000;
/**
 * Most recent event timestamp across a pod's agents, PM and order
 * rail.  Returns 0 when nothing has ever landed.  Cheap to recompute
 * every frame since the pod has at most 6 agents + 1 PM + 1 order.
 */
function podLatestEventAtMs(pod: SceneSelectedPod): number {
  let max = 0;
  for (const agent of Object.values(pod.agents)) {
    if (!agent?.lastUpdatedAt) continue;
    const t = new Date(agent.lastUpdatedAt).getTime();
    if (t > max) max = t;
  }
  if (pod.pm.decisionAt) {
    const t = new Date(pod.pm.decisionAt).getTime();
    if (t > max) max = t;
  }
  const ord = pod.recentOrders[0];
  if (ord) {
    const t = new Date(ord.ts).getTime();
    if (t > max) max = t;
  }
  return max;
}

/**
 * Stable phase offset derived from the actor name.  Per-character
 * offsets keep six agents from breathing / blinking in lockstep,
 * which is the surest tell that they're an animation grid rather
 * than independent characters.
 */
function actorPhase(actor: string): number {
  let h = 0;
  for (let i = 0; i < actor.length; i++) h = (h * 31 + actor.charCodeAt(i)) | 0;
  return Math.abs(h);
}

interface BreathParams {
  period: number;
  amp: number;
}

function breathFor(mood: string): BreathParams {
  switch (mood) {
    case 'worried':
      return { period: 1100, amp: 0.012 };
    case 'confident':
    case 'proud':
      return { period: 1500, amp: 0.025 };
    case 'angry':
      return { period: 900, amp: 0.008 };
    case 'celebrating':
    case 'excited':
      return { period: 700, amp: 0.030 };
    default:
      return { period: 1400, amp: 0.018 };
  }
}

/** Per-mood blink frequency.  Angry = rapid stress blinks, confident
 *  = poised, rare blinks, default = ~3.7s.  The actor phase adds up
 *  to ~1.3s jitter so blinks don't fire on the same frame. */
function blinkPeriodFor(mood: string, phase: number): number {
  const base =
    mood === 'angry' ? 2500 : mood === 'confident' || mood === 'proud' ? 5500 : 3700;
  return base + (phase % 1300);
}

interface IdleFx {
  breathScaleY: number;
  tilt: number;
  blinking: boolean;
  eyeShiftX: number;
  eyeShiftY: number;
}

/**
 * Ambient idle animation parameters for one character on this frame.
 *
 *   * breathScaleY -- scale.y multiplier (always near 1.0).  Caller
 *     uses scale.y only (no horizontal stretch) so the head doesn't
 *     deform; this reads as a chest rise.
 *   * tilt -- subtle rotation in radians, applied to the role icon.
 *   * blinking -- when true, eyes render as a thin line for one frame
 *     and the surrounding ~110 ms.
 *   * eyeShiftX / Y -- pupil offsets in head-radius units, used during
 *     the reaction window to make eyes "look at" something.
 */
function computeIdleFx(
  actor: string,
  mood: string,
  intensity: string | null,
  tick: number,
  reactionAgeMs: number,
  reactionKind: string | null,
  sleeping: boolean,
): IdleFx {
  const phase = actorPhase(actor);
  const mul = intensityToMultiplier(intensity);

  const b = breathFor(mood);
  let breathScaleY = 1 + Math.sin((tick + phase) / b.period) * b.amp * mul;
  if (mood === 'frozen') {
    breathScaleY += Math.sin((tick + phase) / 80) * 0.005;
  }
  if (sleeping) {
    // Slower, deeper breathing when the pod is dormant.
    breathScaleY = 1 + Math.sin((tick + phase) / 2400) * 0.028;
  }

  const tilt = Math.sin((tick + phase) / 2200) * 0.012;

  // Blink when sleeping is effectively "always closed".  Otherwise
  // use the per-mood period.
  let blinking = sleeping;
  if (!sleeping) {
    const period = blinkPeriodFor(mood, phase);
    const sinceBlink = (tick + phase) % period;
    blinking = sinceBlink < 110;
  }

  let eyeShiftX = 0;
  let eyeShiftY = 0;
  if (!sleeping && reactionAgeMs >= 0 && reactionAgeMs < IDLE_REACTION_MS && reactionKind) {
    if (/candle|chart|tape|rsi|macd|heatmap|sparkline|pattern/i.test(reactionKind)) {
      // Chart-watching: eyes track down + slight horizontal sweep.
      eyeShiftY = 0.10;
      eyeShiftX = Math.sin(tick / 240) * 0.06;
    } else if (/twitter|news|rbc|reuter|headline|tone|tweet/i.test(reactionKind)) {
      // News scanning: discrete left/right saccades every ~130 ms.
      eyeShiftX = (Math.sin(tick / 130) > 0 ? 1 : -1) * 0.12;
    } else if (/var|breach|concentration|limit|cro|risk|guideline/i.test(reactionKind)) {
      // Risk checks: eyes narrow + focus straight ahead (no shift).
    } else if (/coffee|window|invalid|catalysts/i.test(reactionKind)) {
      // Contemplative -- eyes drift up.
      eyeShiftY = -0.08;
    } else if (/quarterly|fundamentals|multiples|dcf|filing/i.test(reactionKind)) {
      // Document reading: eyes down + slightly right (lines on a page).
      eyeShiftY = 0.08;
      eyeShiftX = 0.06;
    } else if (/broker|order|fill|book|rule/i.test(reactionKind)) {
      // EA tape / discipline: eyes scan right.
      eyeShiftX = 0.10;
    }
  }

  return { breathScaleY, tilt, blinking, eyeShiftX, eyeShiftY };
}

export class PodRoomScene implements Scene {
  private root: Container | null = null;
  private background: Graphics | null = null;
  private agentSprites = new Map<string, AgentSprite>();
  private pmSprite: PmSprite | null = null;
  private tickerRail: Container | null = null;
  /** Rail rect: [x, y, w, h] of the order-ticker strip in canvas coords. */
  private tickerRailRect: [number, number, number, number] = [0, 0, 0, 0];
  /** Stored order-chip signature so we only rebuild the rail when the
   * orders list changes (else every frame redraws all chips). */
  private orderRailSig = '';
  /** Layer for the EA "paper" projectile flying to the ticker rail. */
  private projectiles: Graphics | null = null;
  /** First-seen ts (perf.now ms) for each order id -- used to time the
   * EA -> rail flight animation. */
  private orderSeenAt = new Map<string, number>();
  private signature = '';

  enter(_ctx: SceneContext, container: Container): void {
    this.root = new Container();
    container.addChild(this.root);
  }

  exit(): void {
    this.agentSprites.clear();
    this.pmSprite = null;
    this.background = null;
    this.tickerRail = null;
    this.tickerRailRect = [0, 0, 0, 0];
    this.orderRailSig = '';
    this.projectiles = null;
    this.orderSeenAt.clear();
    this.root?.destroy({ children: true });
    this.root = null;
    this.signature = '';
    // Clear the hit-rect registry so the DOM tooltip layer doesn't
    // try to hover a character that no longer exists -- the next
    // pod-room ``rebuild`` will repopulate it.
    clearPodRoomAgentRects();
  }

  redraw(ctx: SceneContext): void {
    if (!this.root) return;
    const w = ctx.app.renderer.width;
    const h = ctx.app.renderer.height;
    const pod = ctx.getState().selectedPod;
    // Layout depends on the actor list (which is static here) plus
    // viewport size + the selected pod id.  Re-layout when any of
    // those change; bubble contents update every frame via ``tick``.
    const sig = `${w}x${h}|${pod?.podId ?? '-'}|${pod?.floor ?? '-'}`;
    if (sig !== this.signature) {
      this.signature = sig;
      this.rebuild(ctx, pod, w, h);
    }
    this.refreshDynamic(ctx, pod);
  }

  tick(ctx: SceneContext): void {
    if (!this.root) return;
    // Falling through to redraw catches viewport resizes and TTL fades
    // in one pass.  Re-layout only happens when the signature changes.
    this.redraw(ctx);
  }

  private rebuild(
    _ctx: SceneContext,
    pod: SceneSelectedPod | null,
    w: number,
    h: number,
  ): void {
    if (!this.root) return;
    this.root.removeChildren();
    this.agentSprites.clear();
    this.pmSprite = null;

    // 1) Floor-tinted background -- smooth vertical gradient from the
    // floor's accent colour down to near-black.  Pixi 8.5+ wants the
    // options-object form of FillGradient -- positional args are
    // deprecation-warned in console.
    const [top, bottom] = FLOOR_GRADIENT[pod?.floor ?? 'default'] ??
      FLOOR_GRADIENT.default ?? [0x21262d, 0x0d1117];
    const gradient = new FillGradient({
      start: { x: 0, y: 0 },
      end: { x: 0, y: h },
    });
    gradient.addColorStop(0, top);
    gradient.addColorStop(1, bottom);
    this.background = new Graphics().rect(0, 0, w, h).fill(gradient);
    this.root.addChild(this.background);

    // 2) Layout decision: wide ring vs narrow 2x3 grid.
    const isNarrow = w < 600;
    const layout = isNarrow
      ? layoutPodRoomNarrow(w, h)
      : layoutPodRoomWide(w, h);

    // 3) PM in the centre / top.
    this.pmSprite = this.buildPmSprite(layout.pm);
    this.root.addChild(this.pmSprite.container);

    // 4) Agents.
    for (let i = 0; i < AGENT_RING.length; i++) {
      const slot = layout.agents[i];
      const spec = AGENT_RING[i];
      if (!slot || !spec) continue;
      const sprite = this.buildAgentSprite(spec.actor, spec.label, slot);
      this.agentSprites.set(spec.actor, sprite);
      this.root.addChild(sprite.container);
    }

    // 5) Trading-floor ticker rail at the bottom edge.  The rail is a
    // dedicated Container so we can wipe & repopulate just the chips
    // when ``recentOrders`` changes -- no need to touch the rest of
    // the scene.
    this.tickerRail = new Container();
    this.tickerRailRect = [...layout.rail];
    this.root.addChild(this.tickerRail);
    // Rail backdrop -- thin translucent bar pinned to the canvas
    // bottom so empty rooms still telegraph "the trading floor lives
    // here".
    const [rx, ry, rw, rh] = layout.rail;
    const railBg = new Graphics()
      .rect(rx, ry, rw, rh)
      .fill({ color: 0x0a0e14, alpha: 0.72 })
      .moveTo(rx, ry)
      .lineTo(rx + rw, ry)
      .stroke({ width: 1, color: 0x21262d });
    this.tickerRail.addChild(railBg);
    // Reset the chip signature so refreshDynamic populates the rail.
    this.orderRailSig = '';

    // 6) Projectile layer sits ABOVE everything else so the flying
    // order paper draws over the heads/rail/bubbles.
    this.projectiles = new Graphics();
    this.root.addChild(this.projectiles);

    // 7) Publish hit-rects to the DOM tooltip overlay.  The overlay
    // hit-tests circular regions in CSS-pixel space; we feed it the
    // same ``AgentSlot`` coords we just used for placement, plus PM.
    // No DPI conversion: PixiCanvas uses ``autoDensity`` so Pixi's
    // pixel-space equals the host div's CSS pixel-space.
    const hitRects: AgentHitRect[] = [];
    for (let i = 0; i < AGENT_RING.length; i++) {
      const slot = layout.agents[i];
      const spec = AGENT_RING[i];
      if (!slot || !spec) continue;
      hitRects.push({
        actor: spec.actor,
        label: spec.label,
        cx: slot.x,
        cy: slot.y,
        radius: slot.radius,
      });
    }
    hitRects.push({
      actor: 'pm',
      label: 'Portfolio Manager',
      cx: layout.pm.x,
      cy: layout.pm.y,
      radius: layout.pm.radius,
    });
    setPodRoomAgentRects(hitRects);
  }

  private buildPmSprite(slot: AgentSlot): PmSprite {
    const container = new Container();
    container.position.set(slot.x, slot.y);
    container.eventMode = 'static';

    // Burst rays painted briefly when pm.decision_made lands.  Lives
    // BEHIND the head so the rays look like they shoot out from the
    // PM rather than over his face.
    const burst = new Graphics();
    container.addChild(burst);

    const ring = new Graphics();
    container.addChild(ring);

    // Face features -- repainted every frame in ``refreshDynamic`` so
    // the mood drives the expression (smile / frown / sparkle eyes...).
    const face = new Graphics();
    container.addChild(face);

    // Crown icon hovering just above the head.
    const roleIcon = new Graphics();
    roleIcon.position.set(0, -slot.radius - slot.fontSize * 0.6);
    container.addChild(roleIcon);

    const caption = new Text({
      text: 'Portfolio Manager',
      style: new TextStyle({
        fill: 0x8b949e,
        fontFamily: 'system-ui, sans-serif',
        fontSize: Math.max(9, slot.fontSize - 2),
      }),
    });
    caption.anchor.set(0.5, 0);
    caption.position.set(0, slot.radius + 8);
    container.addChild(caption);

    const bubbleBg = new Graphics();
    container.addChild(bubbleBg);

    const bubbleText = new Text({
      text: '',
      style: new TextStyle({
        fill: 0xe6edf3,
        fontFamily: 'system-ui, sans-serif',
        fontSize: Math.max(10, slot.fontSize - 2),
        wordWrap: true,
        wordWrapWidth: slot.bubbleWidth - 16,
        align: 'center',
      }),
    });
    bubbleText.anchor.set(0.5, 1);
    container.addChild(bubbleText);

    // ZZZ mark for dormant pods -- sits above the crown, invisible
    // until ``refreshDynamic`` flips ``visible`` based on activity.
    const zzz = new Text({
      text: 'z z z',
      style: new TextStyle({
        fill: 0x8b949e,
        fontFamily: 'system-ui, sans-serif',
        fontSize: Math.max(11, slot.fontSize),
        letterSpacing: 2,
      }),
    });
    zzz.anchor.set(0.5, 1);
    zzz.position.set(slot.radius * 0.6, -slot.radius * 1.5);
    zzz.visible = false;
    container.addChild(zzz);

    // Stash slot geometry on the sprite for the refreshDynamic pass.
    (container as Container & { _slot?: AgentSlot })._slot = slot;
    return { container, ring, face, roleIcon, burst, caption, bubbleBg, bubbleText, zzz };
  }

  private buildAgentSprite(actor: string, label: string, slot: AgentSlot): AgentSprite {
    const container = new Container();
    container.position.set(slot.x, slot.y);
    container.eventMode = 'static';

    // Burst halo painted briefly when a signal lands -- behind the head
    // so it looks like the head is the source of the pulse.
    const burst = new Graphics();
    container.addChild(burst);

    const ring = new Graphics();
    container.addChild(ring);

    // Face -- mood-driven, repainted every frame.
    const face = new Graphics();
    container.addChild(face);

    // Role-specific accessory hovering above the head.  Each agent's
    // icon (bar chart for QA, speech bubble for SA, ...) tells the
    // operator at-a-glance what that seat does without reading the
    // 2-letter abbreviation that the old badge carried.
    const roleIcon = new Graphics();
    roleIcon.position.set(0, -slot.radius - slot.fontSize * 0.5);
    container.addChild(roleIcon);

    const caption = new Text({
      text: label,
      style: new TextStyle({
        fill: 0x8b949e,
        fontFamily: 'system-ui, sans-serif',
        fontSize: Math.max(9, slot.fontSize - 3),
      }),
    });
    caption.anchor.set(0.5, 0);
    caption.position.set(0, slot.radius + 6);
    container.addChild(caption);

    const bubbleBg = new Graphics();
    container.addChild(bubbleBg);

    const bubbleText = new Text({
      text: '',
      style: new TextStyle({
        fill: 0xe6edf3,
        fontFamily: 'system-ui, sans-serif',
        fontSize: Math.max(9, slot.fontSize - 3),
        wordWrap: true,
        wordWrapWidth: slot.bubbleWidth - 16,
        align: 'center',
      }),
    });
    bubbleText.anchor.set(0.5, 1);
    container.addChild(bubbleText);

    (container as Container & { _slot?: AgentSlot })._slot = slot;
    return { actor, container, ring, face, roleIcon, burst, caption, bubbleBg, bubbleText };
  }

  private refreshDynamic(ctx: SceneContext, pod: SceneSelectedPod | null): void {
    const now = Date.now();
    const tick = performance.now();
    const sunPhase = ctx.getState().sunPhase ?? null;
    // Latest event on this pod, in ms-since-epoch.  Drawn from agent
    // ``lastUpdatedAt`` + PM ``decisionAt``; ``recentOrders[0]`` is a
    // tie-breaker for pods that only ever submit orders without
    // intermediate agent chatter.  ``0`` means "no event ever".
    const latestEventAtMs = pod ? podLatestEventAtMs(pod) : 0;
    // A pod is "dormant" when its latest event is more than ~2 min
    // old.  Night sky also reads as dormant -- agents are off shift.
    const isNightSky = sunPhase === 'night' || sunPhase === 'predawn';
    const dormant =
      isNightSky ||
      (latestEventAtMs > 0 && now - latestEventAtMs > POD_DORMANT_THRESHOLD_MS);

    // Proximity awareness: collect "active speaker" slots so PM and
    // each agent can glance toward whoever last spoke (AI Town /
    // Gather style).  An active speaker = agent whose lastUpdatedAt
    // is within ``LOOK_DURATION_MS`` and isn't currently in a thinking
    // bubble (the thinking ``...`` isn't a "statement" worth turning
    // heads to -- it's pre-speech).  PM's own decision counts so
    // agents look at the PM when a decision lands.
    const speakers: LookSpeaker[] = [];
    if (pod) {
      for (const spec of AGENT_RING) {
        const agent = pod.agents[spec.actor];
        if (!agent?.lastUpdatedAt || agent.thinking) continue;
        const age = now - new Date(agent.lastUpdatedAt).getTime();
        if (age < 0 || age >= LOOK_DURATION_MS) continue;
        const slot = (
          this.agentSprites.get(spec.actor)?.container as
            | (Container & { _slot?: AgentSlot })
            | undefined
        )?._slot;
        if (!slot) continue;
        speakers.push({ actor: spec.actor, x: slot.x, y: slot.y, ageMs: age });
      }
      if (pod.pm.decisionAt) {
        const age = now - new Date(pod.pm.decisionAt).getTime();
        if (age >= 0 && age < LOOK_DURATION_MS && this.pmSprite) {
          const slot = (
            this.pmSprite.container as Container & { _slot?: AgentSlot }
          )._slot;
          if (slot) {
            speakers.push({ actor: 'pm', x: slot.x, y: slot.y, ageMs: age });
          }
        }
      }
    }

    // PM
    if (this.pmSprite) {
      const slot = (
        this.pmSprite.container as Container & { _slot?: AgentSlot }
      )._slot!;
      const pm = pod?.pm;
      const isThinking = pm?.thinking ?? false;
      const intentsZero = (pm?.intentsCount ?? null) === 0;
      const accent = intentsZero ? MOOD_COLOR.worried : MOOD_COLOR.confident;
      const accentColor = accent ?? 0x2ea043;
      // PM body fill = the cream "head" colour so the face features
      // contrast cleanly; the accent paints the ring only.
      this.pmSprite.ring
        .clear()
        .circle(0, 0, slot.radius)
        .fill({ color: 0xf2e8c9, alpha: 0.92 })
        .circle(0, 0, slot.radius)
        .stroke({ width: 3, color: accentColor });
      const pmMood = isThinking
        ? 'thinking'
        : intentsZero
          ? 'worried'
          : 'confident';
      // Ambient idle FX for PM -- breathing + blink + tilt -- only
      // when not in active "thinking" mode (that has its own bigger
      // pulse below).  PM never has a phrase kind so we pass null;
      // sleeping is dormant-pod-wide.
      const pmFx = computeIdleFx(
        'pm',
        pmMood,
        null,
        tick,
        -1,
        null,
        dormant,
      );
      // Proximity: glance toward whichever agent just spoke.  PM sits
      // at the centre so the look reads as "listening".  Skipped when
      // thinking (own pulse owns the focus) or sleeping.
      const pmLook =
        !isThinking && !dormant
          ? pickLookOffset('pm', slot.x, slot.y, speakers)
          : null;
      this.pmSprite.face.clear();
      drawFace(this.pmSprite.face, slot.radius, pmMood, 0x0d1117, {
        blinking: pmFx.blinking,
        eyeShiftX: pmLook?.eyeX ?? pmFx.eyeShiftX,
        eyeShiftY: pmLook?.eyeY ?? pmFx.eyeShiftY,
      });
      // Crown: tilts with the bigger sin while thinking, otherwise
      // micro-tilts from the ambient layer plus the look bias.
      this.pmSprite.roleIcon.clear();
      const crownSize = slot.radius * 0.85;
      drawRoleIcon(this.pmSprite.roleIcon, 'pm', 0, 0, crownSize, 0xffd33d);
      this.pmSprite.roleIcon.rotation = isThinking
        ? Math.sin(tick / 200) * 0.08
        : pmFx.tilt + (pmLook?.tilt ?? 0);
      // Container scale: thinking pulse on x+y, otherwise ambient
      // breathing on y only (chest rise, not horizontal squish).
      if (isThinking) {
        this.pmSprite.container.scale.set(1 + Math.sin(tick / 220) * 0.04);
      } else {
        this.pmSprite.container.scale.set(1, pmFx.breathScaleY);
      }

      // PM decision-burst: 8 rays radiating outward, fades in 1.2 s.
      this.pmSprite.burst.clear();
      const decisionAgeMs = pm?.decisionAt
        ? now - new Date(pm.decisionAt).getTime()
        : Infinity;
      if (decisionAgeMs < DECISION_BURST_MS) {
        const p = decisionAgeMs / DECISION_BURST_MS;
        drawDecisionBurst(
          this.pmSprite.burst,
          slot.radius,
          accentColor,
          p,
        );
      }

      const summaryText = pm?.lastUiSummary ?? '';
      const ageMs = pm?.decisionAt ? now - new Date(pm.decisionAt).getTime() : Infinity;
      const showBubble = !isThinking && summaryText && ageMs < PM_BUBBLE_TTL_MS;
      drawBubble({
        bg: this.pmSprite.bubbleBg,
        text: this.pmSprite.bubbleText,
        accent: accentColor,
        slot,
        visible: Boolean(isThinking || showBubble),
        content: isThinking ? 'PM analysing…' : truncate(summaryText, 120),
      });
      // Dormant indicator: gently floating "z z z" mark above the
      // crown.  Hidden whenever any bubble (thinking or decision) is
      // showing so the bubble's content stays the visual focus.
      this.pmSprite.zzz.visible = dormant && !isThinking && !showBubble;
      if (this.pmSprite.zzz.visible) {
        this.pmSprite.zzz.alpha = 0.55 + 0.25 * Math.sin(tick / 700);
        this.pmSprite.zzz.position.y = -slot.radius * 1.5 + Math.sin(tick / 900) * 2;
      }
    }

    // Agents
    for (const spec of AGENT_RING) {
      const sprite = this.agentSprites.get(spec.actor);
      if (!sprite) continue;
      const slot = (
        sprite.container as Container & { _slot?: AgentSlot }
      )._slot!;
      const agent = pod?.agents[spec.actor];
      const mood = agent?.mood ?? 'idle';
      const accent = MOOD_COLOR[mood] ?? MOOD_COLOR.idle ?? 0x8b949e;
      const thinking = agent?.thinking ?? false;
      // Animation speed / scale multiplier driven by scene_hint.intensity.
      //   low      → 0.7   (calmer idle motion)
      //   medium   → 1.0   (default)
      //   high     → 1.4   (visibly more urgent)
      //   critical → 2.0   (alarmed)
      const intMul = intensityToMultiplier(agent?.intensity ?? null);

      // Head: cream-coloured "skin" fill + mood-tinted ring stroke.
      // Stroke thickens with intensity so a critical event is readable
      // at a glance even when the user isn't looking at the bubble.
      sprite.ring
        .clear()
        .circle(0, 0, slot.radius)
        .fill({ color: 0xf2e8c9, alpha: 0.94 })
        .circle(0, 0, slot.radius)
        .stroke({ width: 2.5 * Math.max(1, intMul * 0.85), color: accent });
      // Ambient idle FX (breathing + blink + tilt + eye saccade).
      // Reaction window: time since lastPhraseAt drives kind-specific
      // eye/head movements (chart-reading vs news-scanning vs ...).
      const reactionAgeMs = agent?.lastPhraseAt
        ? now - new Date(agent.lastPhraseAt).getTime()
        : Number.POSITIVE_INFINITY;
      const fx = computeIdleFx(
        spec.actor,
        mood,
        agent?.intensity ?? null,
        tick,
        reactionAgeMs,
        agent?.lastPhraseKind ?? null,
        dormant,
      );
      // Proximity: glance at the latest other speaker.  An agent
      // doing its own action (fresh idle_action) keeps its own eye
      // direction -- self-action wins over peer attention.
      const selfActing = reactionAgeMs < IDLE_REACTION_MS;
      const look =
        !thinking && !dormant && !selfActing
          ? pickLookOffset(spec.actor, slot.x, slot.y, speakers)
          : null;
      sprite.face.clear();
      drawFace(sprite.face, slot.radius, mood, 0x0d1117, {
        blinking: fx.blinking,
        eyeShiftX: look?.eyeX ?? fx.eyeShiftX,
        eyeShiftY: look?.eyeY ?? fx.eyeShiftY,
      });
      sprite.roleIcon.clear();
      const iconSize = slot.radius * 0.95;
      drawRoleIcon(sprite.roleIcon, spec.actor, 0, 0, iconSize, accent);
      // Role icon: thinking pulse > ambient micro-tilt + look bias.
      sprite.roleIcon.rotation = thinking
        ? Math.sin((tick * intMul) / 160 + slot.x) * 0.12 * intMul
        : fx.tilt + (look?.tilt ?? 0);
      // Container scale: thinking pulse (x+y) vs idle breathing (y only).
      if (thinking) {
        sprite.container.scale.set(
          1 + Math.sin((tick * intMul) / 180 + slot.x) * 0.05 * intMul,
        );
      } else {
        sprite.container.scale.set(1, fx.breathScaleY);
      }

      // Signal-emit burst: an expanding halo painted for ~800 ms after
      // ``agent.signal_emitted`` lands.  The age is read off the
      // agent's ``lastUpdatedAt`` rather than tracked separately, so
      // we don't need a new ref in the React layer.
      sprite.burst.clear();
      if (agent?.lastUpdatedAt && !thinking) {
        const ageMs = now - new Date(agent.lastUpdatedAt).getTime();
        if (ageMs >= 0 && ageMs < SIGNAL_BURST_MS) {
          const p = ageMs / SIGNAL_BURST_MS;
          drawSignalBurst(sprite.burst, slot.radius, accent, p);
        }
      }

      const ageMs = agent?.lastUpdatedAt
        ? now - new Date(agent.lastUpdatedAt).getTime()
        : Infinity;
      // ``agent.idle_action`` also opens a bubble (the phrase the
      // backend emitted) -- check both signal age + phrase age so a
      // fresh phrase keeps the bubble alive even when no recent
      // signal exists.
      const phraseAgeMs = agent?.lastPhraseAt
        ? now - new Date(agent.lastPhraseAt).getTime()
        : Infinity;
      const showBubble =
        agent &&
        !thinking &&
        (ageMs < SIGNAL_BUBBLE_TTL_MS || phraseAgeMs < SIGNAL_BUBBLE_TTL_MS);
      const content = thinking ? '…' : agentBubbleText(agent ?? null);
      drawBubble({
        bg: sprite.bubbleBg,
        text: sprite.bubbleText,
        accent,
        slot,
        visible: Boolean(thinking || (showBubble && content)),
        content,
      });
    }

    // Trading-floor ticker rail
    this.refreshOrderRail(pod?.recentOrders ?? []);

    // Order-flight: animate the newest order from the EA seat over to
    // the ticker rail.
    this.refreshOrderProjectiles(pod?.recentOrders ?? [], tick);
  }

  /**
   * Project the newest in-flight order across the scene as a small
   * "paper" rectangle.  Stamps a perf.now timestamp the first time we
   * see an order id, then for ~ORDER_FLIGHT_MS afterwards draws it at
   * an eased position between the EA agent and the rail target.
   */
  private refreshOrderProjectiles(
    orders: readonly SceneOrder[],
    tick: number,
  ): void {
    if (!this.projectiles) return;
    this.projectiles.clear();
    if (orders.length === 0) return;

    // Record first-seen for any new orders.
    for (const o of orders) {
      if (!this.orderSeenAt.has(o.id)) {
        this.orderSeenAt.set(o.id, tick);
      }
    }
    // GC the map: keep only ids that are still in the recent list.
    if (this.orderSeenAt.size > orders.length * 2) {
      const live = new Set(orders.map((o) => o.id));
      for (const id of [...this.orderSeenAt.keys()]) {
        if (!live.has(id)) this.orderSeenAt.delete(id);
      }
    }

    const ea = this.agentSprites.get('ea');
    if (!ea) return;
    const eaSlot = (ea.container as Container & { _slot?: AgentSlot })._slot;
    if (!eaSlot) return;
    const [rx, ry, , rh] = this.tickerRailRect;

    // Only animate orders that landed within the flight window.
    for (let i = 0; i < orders.length; i++) {
      const order = orders[i];
      if (!order) continue;
      const seenAt = this.orderSeenAt.get(order.id);
      if (seenAt == null) continue;
      const age = tick - seenAt;
      if (age < 0 || age >= ORDER_FLIGHT_MS) continue;

      const t = age / ORDER_FLIGHT_MS;
      // ease-out (1 - (1-t)^3)
      const eased = 1 - Math.pow(1 - t, 3);

      // Target slot: ``i`` is the position in the rail (newest first).
      // Chip widths are ~80-160 px; approximate with a 110 px stride.
      const targetX = rx + 16 + i * 110 + 40;
      const targetY = ry + rh / 2;

      const x = eaSlot.x + (targetX - eaSlot.x) * eased;
      const y = eaSlot.y + (targetY - eaSlot.y) * eased;
      const rotation = eased * Math.PI * 2;
      const accent =
        order.type === 'filled'
          ? 0x2ea043
          : order.type === 'rejected'
            ? 0xf85149
            : 0x58a6ff;
      drawPaperProjectile(this.projectiles, x, y, rotation, accent, 1 - t * 0.4);
    }
  }

  /**
   * Repopulate the bottom ticker rail with chips for the most recent
   * orders.  Only redraws when the order list changes.
   */
  private refreshOrderRail(orders: readonly SceneOrder[]): void {
    if (!this.tickerRail) return;
    const slice = orders.slice(0, ORDER_CHIP_VISIBLE_MAX);
    // Signature: ids only -- the chips themselves are static, so the
    // sig changes only when a new order lands or an old one rolls off.
    const sig = slice.map((o) => `${o.id}|${o.type}`).join(',');
    if (sig === this.orderRailSig) return;
    this.orderRailSig = sig;

    // Wipe everything except the first child (the rail backdrop) and
    // re-add chips.  Keeps the backdrop GC-stable across redraws.
    while (this.tickerRail.children.length > 1) {
      const dead = this.tickerRail.children[this.tickerRail.children.length - 1];
      if (!dead) break;
      this.tickerRail.removeChild(dead);
      dead.destroy({ children: true });
    }

    const [rx, ry, rw, rh] = this.tickerRailRect;
    if (slice.length === 0) {
      // Idle hint -- a small "no orders yet" label centred in the rail.
      const idle = new Text({
        text: 'Trading floor idle — awaiting orders.',
        style: new TextStyle({
          fill: 0x6e7681,
          fontFamily: 'system-ui, sans-serif',
          fontSize: Math.max(10, Math.min(13, rh - 14)),
          fontStyle: 'italic',
        }),
      });
      idle.anchor.set(0.5);
      idle.position.set(rx + rw / 2, ry + rh / 2);
      this.tickerRail.addChild(idle);
      return;
    }

    // Chip strip, newest left, oldest right.  Each chip = "TICKER · detail".
    const padX = 10;
    const chipGap = 6;
    const chipH = Math.min(rh - 10, 28);
    let cursorX = rx + padX;
    const chipsToDraw = slice.slice(0, ORDER_CHIP_VISIBLE_MAX);
    const maxRight = rx + rw - padX;
    for (const order of chipsToDraw) {
      const colors = ORDER_COLOR[order.type] ?? ORDER_COLOR.submitted!;
      const chip = new Container();
      // Measure label
      const labelText = new Text({
        text: `${order.ticker || '—'}  ${order.detail || ''}`.trim(),
        style: new TextStyle({
          fill: 0xe6edf3,
          fontFamily: 'system-ui, sans-serif',
          fontSize: 11,
          fontWeight: '600',
        }),
      });
      const labelW = Math.min(160, labelText.width + padX * 2);
      const chipW = labelW;
      if (cursorX + chipW > maxRight) break;
      const bg = new Graphics()
        .roundRect(0, 0, chipW, chipH, chipH / 2)
        .fill({ color: colors.bg, alpha: 0.92 })
        .stroke({ width: 1.5, color: colors.stroke });
      labelText.anchor.set(0.5);
      labelText.position.set(chipW / 2, chipH / 2);
      chip.addChild(bg);
      chip.addChild(labelText);
      chip.position.set(cursorX, ry + (rh - chipH) / 2);
      this.tickerRail.addChild(chip);
      cursorX += chipW + chipGap;
    }
  }
}

interface AgentSlot {
  x: number;
  y: number;
  radius: number;
  fontSize: number;
  bubbleWidth: number;
  /** Where the bubble sits relative to the avatar. */
  bubbleSide: 'top' | 'bottom';
}

interface PodRoomLayout {
  pm: AgentSlot;
  agents: AgentSlot[];
  /** Bottom ticker rail rect [x, y, w, h]. */
  rail: [number, number, number, number];
}

function layoutPodRoomWide(w: number, h: number): PodRoomLayout {
  // Reserve a 44 px ticker rail along the bottom edge for the Phase 7
  // recent-orders strip; the PM + agent ring lays out in the remaining
  // height so the rail never overlaps the avatar circles.
  const railH = 44;
  const usableH = h - railH;
  const centerX = w / 2;
  const centerY = usableH / 2;
  const pmRadius = Math.max(46, Math.min(w, usableH) * 0.11);
  const ringRadius = Math.max(pmRadius * 2.2, Math.min(w, usableH) * 0.32);
  const agentRadius = Math.max(34, Math.min(w, usableH) * 0.07);
  const fontSize = Math.max(12, Math.round(pmRadius * 0.34));

  const agents: AgentSlot[] = AGENT_RING.map((_, i) => {
    // Top-center is angle = -90deg, then clockwise.
    const angle = (-Math.PI / 2) + (i * (2 * Math.PI)) / AGENT_RING.length;
    return {
      x: centerX + Math.cos(angle) * ringRadius,
      y: centerY + Math.sin(angle) * ringRadius,
      radius: agentRadius,
      fontSize: Math.max(10, Math.round(agentRadius * 0.42)),
      bubbleWidth: Math.min(200, ringRadius * 0.95),
      bubbleSide: Math.sin(angle) < -0.2 ? 'bottom' : 'top',
    };
  });

  return {
    pm: {
      x: centerX,
      y: centerY,
      radius: pmRadius,
      fontSize,
      bubbleWidth: Math.min(280, w * 0.6),
      bubbleSide: 'top',
    },
    agents,
    rail: [0, h - railH, w, railH],
  };
}

function layoutPodRoomNarrow(w: number, h: number): PodRoomLayout {
  // Mobile canvas tends to be portrait-shaped (e.g., 375x400 after the
  // app header + pod-room header eat top space).  PM gets a generous
  // top band; the 6 agents tile into a 3x2 grid below so each row has
  // room for both the avatar and its role caption without overlap.
  // Phase 7 ticker rail at the bottom takes 38 px on mobile (a touch
  // shorter than desktop so the agent grid doesn't shrink further).
  const railH = 38;
  const pmRadius = Math.max(36, Math.min(w, h) * 0.13);
  const pmY = 28 + pmRadius;
  const centerX = w / 2;

  const cols = 3;
  const rows = 2;
  const gridTop = pmY + pmRadius + 36;
  // Reserve 24 px + the ticker rail so the bottom-row caption never
  // gets clipped by the rail or the canvas edge (the EventFeed sits
  // flush against it on mobile).
  const gridBottom = h - 24 - railH;
  const gridHeight = Math.max(140, gridBottom - gridTop);
  const cellH = gridHeight / rows;
  const cellW = w / cols;
  // Reserve room for the role caption (~16 px) under each avatar.
  const captionReserve = 22;
  const agentRadius = Math.max(
    18,
    Math.min(
      cellW * 0.36,
      Math.max(16, (cellH - captionReserve) * 0.42),
    ),
  );

  const agents: AgentSlot[] = AGENT_RING.map((_, i) => {
    const row = Math.floor(i / cols);
    const col = i % cols;
    // Anchor the avatar above the caption inside its cell.
    const cellCenterY = gridTop + cellH * (row + 0.5);
    return {
      x: cellW * (col + 0.5),
      y: cellCenterY - captionReserve / 2,
      radius: agentRadius,
      fontSize: Math.max(9, Math.round(agentRadius * 0.45)),
      bubbleWidth: Math.min(130, cellW - 16),
      bubbleSide: 'top',
    };
  });

  return {
    pm: {
      x: centerX,
      y: pmY,
      radius: pmRadius,
      fontSize: Math.max(14, Math.round(pmRadius * 0.36)),
      bubbleWidth: Math.min(280, w - 32),
      bubbleSide: 'bottom',
    },
    agents,
    rail: [0, h - railH, w, railH],
  };
}

function drawBubble({
  bg,
  text,
  accent,
  slot,
  visible,
  content,
}: {
  bg: Graphics;
  text: Text;
  accent: number;
  slot: AgentSlot;
  visible: boolean;
  content: string;
}): void {
  bg.clear();
  if (!visible || !content) {
    text.text = '';
    text.visible = false;
    return;
  }
  text.text = content;
  text.visible = true;
  // Measure text via Pixi's internal bounds AFTER setting it.
  const padding = 10;
  const bounds = text.getBounds();
  const bw = Math.min(slot.bubbleWidth, bounds.width + padding * 2);
  const bh = bounds.height + padding * 2;
  const offsetY =
    slot.bubbleSide === 'top'
      ? -(slot.radius + 12 + bh)
      : slot.radius + 12;
  const bx = -bw / 2;
  bg
    .roundRect(bx, offsetY, bw, bh, 10)
    .fill({ color: 0x0d1117, alpha: 0.95 })
    .stroke({ width: 1.5, color: accent });
  text.position.set(0, offsetY + bh - padding);
}

function agentBubbleText(agent: SceneAgent | null): string {
  if (!agent) return '';
  // When the most recent activity was an ambient ``agent.idle_action``
  // (not a signal), prefer its phrase -- otherwise the bubble would
  // keep showing the last signal's tickers for the rest of the
  // session, which is misleading.  The store updates ``lastPhraseAt``
  // only when an idle_action lands and ``lastUpdatedAt`` only when a
  // signal lands, so comparing the two timestamps tells us which
  // event-stream emitted last.
  if (agent.lastPhrase) {
    const phraseTs = agent.lastPhraseAt ? Date.parse(agent.lastPhraseAt) : 0;
    const signalTs = agent.lastUpdatedAt ? Date.parse(agent.lastUpdatedAt) : 0;
    if (phraseTs > signalTs) {
      // Truncate so the phrase fits the bubble box -- backend phrases
      // run 20-40 chars (e.g. "листает свечные графики"), but defend
      // against edge cases that could overflow.
      return truncate(agent.lastPhrase, 32);
    }
  }
  if (agent.topTickers.length > 0) return agent.topTickers.join(', ');
  if (agent.lastSignal != null) return `signal ${agent.lastSignal.toFixed(2)}`;
  return '';
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

/**
 * Expanding halo painted briefly after ``agent.signal_emitted``.
 * ``progress`` is 0 (just fired) -> 1 (about to disappear); the ring
 * grows to roughly 2.5x the head radius while fading to transparent.
 */
function drawSignalBurst(
  g: Graphics,
  headRadius: number,
  accent: number,
  progress: number,
): void {
  const r1 = headRadius * (1 + progress * 1.5);
  const alpha1 = (1 - progress) * 0.55;
  g.circle(0, 0, r1)
    .stroke({ width: 2.5, color: accent, alpha: alpha1 });
  // Second concentric ring slightly behind for a "double-tap" feel.
  const r2 = headRadius * (1 + progress * 0.8);
  const alpha2 = (1 - progress) * 0.35;
  g.circle(0, 0, r2)
    .stroke({ width: 1.6, color: accent, alpha: alpha2 });
}

/**
 * Tiny rotating "paper" sprite drawn on top of the scene to represent
 * an in-flight order on its way from the EA agent to the ticker rail.
 * The paper has a thin coloured border (filled / submitted / rejected
 * accent) and a centre line so it reads as a slip even at small scale.
 */
function drawPaperProjectile(
  g: Graphics,
  cx: number,
  cy: number,
  rotation: number,
  accent: number,
  alpha: number,
): void {
  const w = 18;
  const h = 12;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  // Rotate a local-frame point ``(lx, ly)`` around the paper centre
  // into world coords.
  const rot = (lx: number, ly: number): [number, number] => [
    cx + lx * cos - ly * sin,
    cy + lx * sin + ly * cos,
  ];
  const tl = rot(-w / 2, -h / 2);
  const tr = rot(w / 2, -h / 2);
  const br = rot(w / 2, h / 2);
  const bl = rot(-w / 2, h / 2);

  g.moveTo(tl[0], tl[1])
    .lineTo(tr[0], tr[1])
    .lineTo(br[0], br[1])
    .lineTo(bl[0], bl[1])
    .closePath()
    .fill({ color: 0xf2e8c9, alpha: alpha * 0.95 })
    .stroke({ width: 1.4, color: accent, alpha });

  // A single "text line" running horizontally across the local
  // frame, rotated with the paper.
  const a = rot(-w / 2 + 3, 0);
  const b = rot(w / 2 - 3, 0);
  g.moveTo(a[0], a[1])
    .lineTo(b[0], b[1])
    .stroke({ width: 1, color: 0x6e7681, alpha: alpha * 0.8 });
}

/**
 * PM decision burst: 8 narrow rays radiating outward, length grows
 * with ``progress`` and the whole thing fades.
 */
function drawDecisionBurst(
  g: Graphics,
  headRadius: number,
  accent: number,
  progress: number,
): void {
  const rayCount = 8;
  const inner = headRadius * 1.05;
  const outer = headRadius * (1.4 + progress * 1.6);
  const alpha = (1 - progress) * 0.85;
  const thickness = headRadius * 0.06;
  for (let i = 0; i < rayCount; i++) {
    const angle = (i / rayCount) * 2 * Math.PI;
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    const x1 = dx * inner;
    const y1 = dy * inner;
    const x2 = dx * outer;
    const y2 = dy * outer;
    // Perpendicular offset for ray "width".
    const px = -dy * thickness;
    const py = dx * thickness;
    g.moveTo(x1 + px, y1 + py)
      .lineTo(x2, y2)
      .lineTo(x1 - px, y1 - py)
      .closePath()
      .fill({ color: accent, alpha });
  }
  // A soft halo behind the rays.
  g.circle(0, 0, outer * 0.55)
    .fill({ color: 0xffd33d, alpha: (1 - progress) * 0.12 });
}
